import { useState, useMemo, useCallback, useEffect } from "react";
import { Select } from "antd";
import trans from "../../../../utils";

import AntCheckBox from "../../../FormBuilderInputs/CheckBox";
import AntDatePicker from "../../../FormBuilderInputs/DatePicker";
import DropdownInput from "../../../FormBuilderInputs/DropdownInput";
import NormalInput from "../../../FormBuilderInputs/NormalInput";
import ContainerPreview from "../../../FormBuilderInputs/ContainerPreview";
import RadioGroup from "../../../FormBuilderInputs/RadioGroup";
import AntTextArea from "../../../FormBuilderInputs/TextArea";
import Toggle from "../../../FormBuilderInputs/Toggle";
import AntButton from "../../../FormBuilderInputs/Button";
import AntHeader from "../../../FormBuilderInputs/Header";
import Image from "../../../FormBuilderInputs/Image";
import Label from "../../../FormBuilderInputs/Label";
import Link from "../../../FormBuilderInputs/Link";
import ProgressCircle from "../../../FormBuilderInputs/ProgressCircle";
import ProgressLine from "../../../FormBuilderInputs/ProgressLine";
import RepeatingGroupPreview from "../../../FormBuilderInputs/RepeatingGroupPreview";
import SnippetCamera from "../../../FormBuilderInputs/Camera";
import {
  getDerivedPropertiesForSource,
  resolveRuntimeValues,
  evaluateVisibilityRules,
} from "../../../FormBuilder/runtimeValues";

function buildValuesMap(fields) {
  const map = {};
  if (!Array.isArray(fields)) return map;
  fields.forEach((field) => {
    if (field?.key) {
      map[field.key] = field.value;
    }
    if (field?.name === "Container" && Array.isArray(field.container)) {
      Object.assign(map, buildValuesMap(field.container));
    }
  });
  return map;
}

function applyValues(fields, valuesMap) {
  if (!Array.isArray(fields)) return [];
  return fields.map((field) => {
    if (field?.name === "Container" && Array.isArray(field.container)) {
      return {
        ...field,
        container: applyValues(field.container, valuesMap),
      };
    }
    return {
      ...field,
      value: valuesMap?.[field.key] !== undefined ? valuesMap[field.key] : field.value,
    };
  });
}

function flattenFields(fields) {
  const result = [];
  if (!Array.isArray(fields)) return result;
  fields.forEach((field) => {
    result.push(field);
    if (field?.name === "Container" && Array.isArray(field.container)) {
      result.push(...flattenFields(field.container));
    }
  });
  return result;
}

export default function EditableFormFields({ schemaFields, valuesFields, onChange }) {
  const baseFields = useMemo(
    () => (Array.isArray(schemaFields) ? schemaFields : []),
    [schemaFields]
  );

  const initialValues = useMemo(
    () => buildValuesMap(valuesFields),
    [valuesFields]
  );

  const [values, setValues] = useState(initialValues);

  // Recompute values only when the actual content of valuesFields changes,
  // not when the array/object identity changes (e.g. parent passes a new []
  // literal every render). Using a content signature avoids infinite loops.
  const valuesSignature = useMemo(
    () =>
      JSON.stringify(
        (Array.isArray(valuesFields) ? valuesFields : []).map((f) => ({
          key: f?.key,
          value: f?.value,
        }))
      ),
    [valuesFields]
  );

  useEffect(() => {
    setValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesSignature]);

  const fieldsWithValues = useMemo(
    () => applyValues(baseFields, values),
    [baseFields, values]
  );

  useEffect(() => {
    onChange(fieldsWithValues);
  }, [fieldsWithValues, onChange]);

  const flatSubmitData = useMemo(
    () => flattenFields(fieldsWithValues),
    [fieldsWithValues]
  );

  const addData = useMemo(
    () => ({
      values: { fields: {} },
      touched: { fields: {} },
      setFieldTouched: () => {},
      setFieldValue: () => {},
    }),
    []
  );

  const updateSubmitData = useCallback(
    (updater) => {
      setValues((prevValues) => {
        const nextArray =
          typeof updater === "function"
            ? updater(flatSubmitData)
            : updater;
        const nextValues = { ...prevValues };
        if (Array.isArray(nextArray)) {
          nextArray.forEach((entry) => {
            if (entry?.key) {
              nextValues[entry.key] = entry.value;
            }
          });
        }
        onChange(applyValues(baseFields, nextValues));
        return nextValues;
      });
    },
    [baseFields, flatSubmitData, onChange]
  );

  const updateTopLevelValue = useCallback(
    (fieldKey, value) => {
      setValues((prevValues) => {
        const nextValues = { ...prevValues, [fieldKey]: value };
        onChange(applyValues(baseFields, nextValues));
        return nextValues;
      });
    },
    [baseFields, onChange]
  );

  const isFieldVisible = (field, scopeValues) => {
    return evaluateVisibilityRules(field?.visibilityRules, scopeValues);
  };

  const getInputType = (item, runtimeScope = null) => {
    const scopeFields = runtimeScope?.fields || baseFields;
    const rawScopeValues = runtimeScope?.values || values;
    const scopeValues = resolveRuntimeValues(scopeFields, rawScopeValues);
    const scopeOnValueChange = runtimeScope?.onValueChange;
    const controlledValue = scopeValues?.[item?.key];
    const scopedSubmitData = scopeFields.map((field) => ({
      key: field.key,
      name: field.name,
      title: field.title,
      value: scopeValues?.[field.key],
    }));
    const scopedSetSubmitData = (updater) => {
      if (!scopeOnValueChange) return;
      const nextData =
        typeof updater === "function" ? updater(scopedSubmitData) : updater;
      if (!Array.isArray(nextData)) return;

      const previousByKey = new Map(
        scopedSubmitData.map((entry) => [entry.key, entry.value])
      );
      nextData.forEach((entry) => {
        if (entry?.key && previousByKey.get(entry.key) !== entry.value) {
          scopeOnValueChange(entry.key, entry.value);
        }
      });
    };
    const scopedAddData = scopeOnValueChange
      ? {
          values: { fields: {} },
          touched: { fields: {} },
          setFieldTouched: () => {},
        }
      : addData;
    const updateScopedValue = (fieldKey, nextValue) => {
      if (scopeOnValueChange) {
        scopeOnValueChange(fieldKey, nextValue);
      } else {
        updateTopLevelValue(fieldKey, nextValue);
      }
    };
    if (!isFieldVisible(item, scopeValues)) return null;

    switch (item?.name) {
      case "Checkbox":
        return (
          <AntCheckBox
            item={item}
            addData={scopedAddData}
            readOnly={false}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
            value={controlledValue}
            onValueChange={scopeOnValueChange ? updateScopedValue : undefined}
          />
        );
      case "DatePicker":
      case "DateTime":
        return (
          <AntDatePicker
            item={item}
            preview={false}
            disable={false}
            addData={scopedAddData}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
          />
        );
      case "Dropdown":
      case "MultiSelect":
        return (
          <DropdownInput
            item={item}
            readOnly={false}
            addData={scopedAddData}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
            value={controlledValue}
            onValueChange={scopeOnValueChange ? updateScopedValue : undefined}
            scopeValues={scopeValues}
            includeProperties={getDerivedPropertiesForSource(
              scopeFields,
              item.key
            )}
          />
        );
      case "Input":
      case "numberinput":
      case "NumberInput":
        return (
          <NormalInput
            item={item}
            readOnly={false}
            addData={scopedAddData}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
            value={controlledValue}
            onValueChange={scopeOnValueChange ? updateScopedValue : undefined}
          />
        );
      case "DerivedValue":
      case "ComputedValue":
        if (item.derivedConfig?.defaultValueOptions) {
          return (
            <Select
              style={{ width: "100%" }}
              value={controlledValue ?? item.derivedConfig.defaultValue ?? null}
              options={(item.derivedConfig.defaultValueOptions.data || []).map((opt) => ({
                label: String(opt),
                value: opt,
              }))}
              onChange={(value) => updateScopedValue(item.key, value)}
              placeholder={trans("Select a value")}
              allowClear
            />
          );
        }
        return (
          <NormalInput
            item={{
              ...item,
              readOnly: true,
              placeholder: trans("Auto-calculated"),
            }}
            preview={false}
            value={controlledValue ?? item.derivedConfig?.defaultValue}
            onValueChange={() => {}}
            readOnly={true}
          />
        );
      case "Container":
        return (
          <ContainerPreview
            getInputType={getInputType}
            ukey={item?.id}
            item={item?.container}
            css={item?.css}
          />
        );
      case "RepeatingGroup":
        return (
          <RepeatingGroupPreview
            getInputType={getInputType}
            item={item}
            value={controlledValue}
            onValueChange={updateTopLevelValue}
          />
        );
      case "Radio_group":
        return (
          <RadioGroup
            item={item}
            readOnly={false}
            addData={scopedAddData}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
          />
        );
      case "Text_area":
        return (
          <AntTextArea
            item={item}
            readOnly={false}
            addData={scopedAddData}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
          />
        );
      case "Toggle":
        return (
          <Toggle
            item={item}
            readOnly={false}
            addData={scopedAddData}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
            value={controlledValue}
            onValueChange={scopeOnValueChange ? updateScopedValue : undefined}
          />
        );
      case "Camera":
        return (
          <SnippetCamera
            item={item}
            addData={scopedAddData}
            submitData={scopeOnValueChange ? scopedSubmitData : flatSubmitData}
            setSubmitData={scopeOnValueChange ? scopedSetSubmitData : updateSubmitData}
            preview={false}
          />
        );
      case "Uploader":
      case "Signature":
        return (
          <div className="display-border p-2 mb-2">
            <strong>{item.title || item.key}</strong>:{" "}
            <span className="text-muted">{trans("Unchanged")}</span>
          </div>
        );
      case "Button":
        return <AntButton item={item} />;
      case "Header":
        return <AntHeader item={item} />;
      case "Image":
        return <Image item={item} />;
      case "Label":
        return <Label item={item} />;
      case "Link":
        return <Link item={item} />;
      case "Progress_circle":
        return <ProgressCircle item={item} />;
      case "Progress_line":
        return <ProgressLine item={item} />;
      default:
        return null;
    }
  };

  if (!baseFields.length) {
    return <div className="text-muted p-2">{trans("No form fields")}</div>;
  }

  return (
    <div className="editable-submission-fields">
      {fieldsWithValues.map((field) => (
        <div key={field?.id || field?.key} className="mb-2" style={field?.css}>
          {getInputType(field)}
        </div>
      ))}
    </div>
  );
}
